import React, { useState, useEffect } from 'react';
import {
  Image as ImageIcon,
  ImageOff,
  RefreshCw,
  AlertTriangle,
  Pipette,
  Trash2,
  ImagePlus,
} from 'lucide-react';
import { getAuthHeaders } from '../../context/AuthContext';
import { resolveImageUrl } from '../../utils/imageUrl';
import { BACKEND_URL } from '../../config/api';
import { showDetectionErrors } from '../../utils/detectionErrors';
import ProcessingDialog from './ProcessingDialog';
import ChangeImageFlow from './ChangeImageFlow';
import MultiColorPickerModal from '../MultiColorPickerModal';

/**
 * Single-color row with confirm-to-delete.
 * Extracted from EditItemForm so all surfaces render colors identically.
 */
const ColorSwatch = ({ color, index, onRemove, confirmingDelete, onConfirmDelete, onCancelDelete }) => {
  if (confirmingDelete) {
    return (
      <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg" data-testid={`color-delete-confirm-${index}`}>
        <div className="w-8 h-8 rounded-md border border-white shadow-sm shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs text-red-700 flex-1">Remove?</span>
        <button type="button" onClick={onConfirmDelete}
          className="px-2.5 py-1 bg-red-600 text-white text-xs rounded-md"
          data-testid={`confirm-delete-color-${index}`}>Yes</button>
        <button type="button" onClick={onCancelDelete}
          className="px-2.5 py-1 border border-red-300 text-red-700 text-xs rounded-md"
          data-testid={`cancel-delete-color-${index}`}>No</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid={`color-swatch-${index}`}>
      <div
        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 border-white shadow-md shrink-0"
        style={{ backgroundColor: color }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-soft">Color {index + 1}</p>
        <p className="text-xs sm:text-sm text-charcoal font-mono">{color}</p>
      </div>
      <button type="button" onClick={() => onRemove(index)}
        className="p-2 text-gray-soft hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
        title="Remove color"
        data-testid={`remove-color-${index}`}>
        <Trash2 size={16} />
      </button>
    </div>
  );
};

/**
 * ImageSection — single source of truth for the product image + color swatches
 * used by Edit Item, Add Item (review step), and AI/URL Add flows.
 *
 * Responsibilities (owned internally):
 *   - Image preview with broken-state recovery
 *   - "Change/Add/Fix Image" → ChangeImageFlow modal
 *   - ProcessingDialog (after image change) to auto-detect colors/sentiments
 *   - Color swatch list with confirm-to-delete
 *   - Manual color picker (MultiColorPickerModal)
 *   - "Rescan colors" with optional warning (only when persisted itemId is given)
 *
 * Props:
 *   imageUrl              - current image URL (any form: R2 https, /api/images/<id>, etc.)
 *   colors                - array of hex strings
 *   onChange({image_url?, thumbnail_url?, colors?, sentiments?, primary_color?, secondary_color?})
 *                         - parent receives any subset of these whenever the user changes them
 *                           or the backend returns detection results.
 *   itemId   (optional)   - if provided, rescan uses /stash/{id}/rescan-colors (persists immediately)
 *   itemName, itemBrand   - passed to ChangeImageFlow for product search
 *   autoOpenChange        - when true, opens ChangeImageFlow on mount
 *   bgWhite               - when true, surfaces inside use bg-white (full-screen mobile)
 *
 * Note: parent retains state ownership of imageUrl + colors. ImageSection is controlled.
 */
const ImageSection = ({
  imageUrl,
  colors = [],
  sentiments = [],
  onChange,
  itemId,
  itemName,
  itemBrand,
  autoOpenChange = false,
  bgWhite = false,
}) => {
  const imagePreview = imageUrl ? resolveImageUrl(imageUrl) : null;
  const hasImage = !!imageUrl;

  const [imageBroken, setImageBroken] = useState(false);
  const [showChangeImage, setShowChangeImage] = useState(autoOpenChange);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Processing-dialog flow (after image change)
  const [showProcessingDialog, setShowProcessingDialog] = useState(false);
  const [detectColors, setDetectColors] = useState(true);
  const [detectSentiments, setDetectSentiments] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingImageUrl, setPendingImageUrl] = useState(null);

  // Rescan
  const [rescanning, setRescanning] = useState(false);
  const [showRescanWarning, setShowRescanWarning] = useState(false);

  // Color confirm-delete
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);

  // Reset broken state whenever the image url changes
  useEffect(() => { setImageBroken(false); }, [imageUrl]);

  // Reset auto-open trigger only on the first render where it was true
  useEffect(() => {
    if (autoOpenChange) setShowChangeImage(true);
  }, [autoOpenChange]);

  const emit = (patch) => onChange?.(patch);

  // ---- Change Image flow ----
  const handleImageSelected = (result) => {
    setShowChangeImage(false);

    const incomingColors = result?.colors || [];
    const incomingSentiments = result?.sentiments || [];
    const hasPreDetected = incomingColors.length > 0 || incomingSentiments.length > 0;

    if (hasPreDetected) {
      // Snap tab already ran detection inline — apply results immediately,
      // skip the ProcessingDialog re-prompt.
      const patch = {
        image_url: result.image_url,
        thumbnail_url: result.thumbnail_url || result.image_url,
      };
      if (incomingColors.length) patch.colors = incomingColors;
      if (incomingSentiments.length) patch.sentiments = [...sentiments, ...incomingSentiments];
      emit(patch);
      return;
    }

    // No detection yet — commit the image and open ProcessingDialog
    emit({
      image_url: result.image_url,
      thumbnail_url: result.thumbnail_url || result.image_url,
    });
    setPendingImageUrl(result.image_url);
    setDetectColors(true);
    setDetectSentiments(true);
    setShowProcessingDialog(true);
  };

  const handleProcessImage = async () => {
    if (!detectColors && !detectSentiments) {
      setShowProcessingDialog(false);
      setPendingImageUrl(null);
      return;
    }
    setIsProcessing(true);
    try {
      const detectMode = (detectColors && detectSentiments) ? 'both' : detectColors ? 'color' : 'sentiment';
      console.log('[ImageSection] handleProcessImage start', { detectMode, pendingImageUrl });
      let response;
      if (pendingImageUrl?.startsWith('http')) {
        const fd = new FormData();
        fd.append('image_url', pendingImageUrl);
        fd.append('detect_mode', detectMode);
        response = await fetch(`${BACKEND_URL}/api/stash/process-image`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: fd,
        });
      } else {
        const imgId = pendingImageUrl?.split('/api/images/')?.pop();
        if (!imgId) throw new Error('Invalid image URL');
        response = await fetch(`${BACKEND_URL}/api/images/${imgId}/process`, {
          method: 'POST', credentials: 'include',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ detect_mode: detectMode }),
        });
      }
      console.log('[ImageSection] process response status', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[ImageSection] process response data', data);
        showDetectionErrors(data);
        const patch = {};
        if (data.colors?.length) patch.colors = data.colors;
        if (data.sentiments?.length) patch.sentiments = [...sentiments, ...data.sentiments];
        if (Object.keys(patch).length) {
          console.log('[ImageSection] emitting patch', patch);
          emit(patch);
        } else {
          console.warn('[ImageSection] no colors/sentiments in response — nothing emitted');
        }
      } else {
        const errText = await response.text();
        console.error('[ImageSection] process-image non-OK', response.status, errText);
        alert(`AI detection failed (status ${response.status}). Please try Rescan after saving.`);
      }
    } catch (e) {
      console.error('Process image error:', e);
      alert(`AI detection error: ${e.message || e}`);
    } finally {
      setIsProcessing(false);
      setShowProcessingDialog(false);
      setPendingImageUrl(null);
    }
  };

  const handleSkipProcessing = () => {
    setShowProcessingDialog(false);
    setPendingImageUrl(null);
  };

  // ---- Rescan ----
  const handleRescanColors = async () => {
    setShowRescanWarning(false);
    setRescanning(true);
    try {
      let response;
      if (itemId) {
        // Persisted item — use rescan endpoint (updates DB)
        response = await fetch(`${BACKEND_URL}/api/stash/${itemId}/rescan-colors`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders(),
        });
      } else {
        // Unsaved item — re-run detection against the current image
        const fd = new FormData();
        fd.append('image_url', imageUrl);
        fd.append('detect_mode', 'color');
        response = await fetch(`${BACKEND_URL}/api/stash/process-image`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: fd,
        });
      }
      if (response.ok) {
        const data = await response.clone().json();
        showDetectionErrors(data);
        emit({ colors: data.colors || [] });
      } else {
        const err = await response.clone().json();
        alert(err.detail || 'Failed to rescan colors');
      }
    } catch (e) {
      console.error('Rescan colors error:', e);
      alert('Failed to rescan colors');
    } finally {
      setRescanning(false);
    }
  };

  // ---- Colors edit ----
  const requestColorRemove = (idx) => setConfirmDeleteIdx(idx);
  const confirmRemoveColor = () => {
    if (confirmDeleteIdx !== null) {
      emit({ colors: colors.filter((_, i) => i !== confirmDeleteIdx) });
      setConfirmDeleteIdx(null);
    }
  };
  const handleColorsConfirmed = (newColors) => {
    emit({ colors: newColors });
    setShowColorPicker(false);
  };

  return (
    <>
      {/* ProcessingDialog — already a fixed-position modal, render directly */}
      {showProcessingDialog && (
        <ProcessingDialog
          imagePreview={imagePreview}
          detectColors={detectColors}
          setDetectColors={setDetectColors}
          detectSentiments={detectSentiments}
          setDetectSentiments={setDetectSentiments}
          onProcess={handleProcessImage}
          onSkip={handleSkipProcessing}
          isProcessing={isProcessing}
        />
      )}

      {/* ChangeImageFlow — content component, needs modal wrapper */}
      {showChangeImage && (
        <div className="fixed inset-0 bg-charcoal/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <ChangeImageFlow
              onImageSelected={handleImageSelected}
              onCancel={() => setShowChangeImage(false)}
              itemName={itemName}
              itemBrand={itemBrand}
            />
          </div>
        </div>
      )}

      {/* MultiColorPicker */}
      {showColorPicker && imagePreview && (
        <MultiColorPickerModal
          imageUrl={imagePreview}
          existingColors={colors}
          onConfirm={handleColorsConfirmed}
          onClose={() => setShowColorPicker(false)}
        />
      )}

      {/* Rescan confirmation */}
      {showRescanWarning && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4" data-testid="rescan-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Rescan will replace all current colors</p>
              <p className="text-xs text-amber-600 mt-1">The AI will re-analyze the image and detect new colors.</p>
              <div className="flex gap-2 mt-3">
                <button type="button" onClick={handleRescanColors}
                  className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700">
                  Rescan Colors
                </button>
                <button type="button" onClick={() => setShowRescanWarning(false)}
                  className="px-3 py-1.5 border border-amber-300 text-amber-700 text-sm rounded-lg hover:bg-amber-100">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image */}
      <div data-testid="image-section" className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-charcoal">Product Image</label>
          <button type="button" onClick={() => setShowChangeImage(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-beige text-charcoal rounded-lg hover:bg-white transition-colors"
            data-testid="change-image-btn">
            <ImagePlus size={14} />
            {imagePreview && !imageBroken ? 'Change' : imageBroken ? 'Fix' : 'Add Image'}
          </button>
        </div>
        <div className={`border-2 border-dashed rounded-lg p-4 text-center border-beige ${bgWhite ? 'bg-white' : ''}`}>
          {imagePreview && !imageBroken ? (
            <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded-lg"
              onError={() => setImageBroken(true)} />
          ) : imagePreview && imageBroken ? (
            <div className="py-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                <ImageOff size={24} className="text-amber-600" />
              </div>
              <p className="text-amber-800 text-sm font-medium">Image Unavailable</p>
            </div>
          ) : (
            <div className="py-6">
              <div className="mx-auto w-12 h-12 rounded-full bg-beige flex items-center justify-center mb-2">
                <ImageIcon size={24} className="text-gray-light" />
              </div>
              <p className="text-sm text-gray-soft">No image</p>
            </div>
          )}
        </div>
      </div>

      {/* Colors */}
      <div data-testid="colors-section" className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-charcoal">
            Colors <span className="text-xs text-gray-soft font-normal ml-1">({colors.length})</span>
          </h3>
          <div className="flex items-center gap-2">
            {hasImage && (
              <>
                <button type="button" onClick={() => setShowRescanWarning(true)} disabled={rescanning}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-beige text-charcoal rounded-lg hover:bg-white disabled:opacity-50"
                  data-testid="rescan-colors-btn">
                  <RefreshCw size={14} className={rescanning ? 'animate-spin' : ''} />
                  {rescanning ? 'Scanning...' : 'Rescan'}
                </button>
                <button type="button" onClick={() => setShowColorPicker(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-sage text-sage rounded-lg hover:bg-sage hover:text-white"
                  data-testid="pick-colors-btn">
                  <Pipette size={14} /> Pick
                </button>
              </>
            )}
          </div>
        </div>
        {colors.length > 0 ? (
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${bgWhite ? 'bg-white p-3 rounded-lg' : ''}`}>
            {colors.map((color, idx) => (
              <ColorSwatch
                key={`${idx}-${color}`}
                color={color}
                index={idx}
                onRemove={requestColorRemove}
                confirmingDelete={confirmDeleteIdx === idx}
                onConfirmDelete={confirmRemoveColor}
                onCancelDelete={() => setConfirmDeleteIdx(null)}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-soft italic">No colors detected.</p>
        )}
      </div>
    </>
  );
};

export default ImageSection;
